import React, {useEffect, useState} from 'react';
import {Box, styled, Tab, Tabs, Typography, useMediaQuery, useTheme, Menu, MenuItem} from '@mui/material';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {NavLink, Outlet, useLocation} from 'react-router-dom';
import {retrieveProfilesByParent} from './Profiles/ProfilesSlice';
import {ROLE_IDS, ROLES} from '../../constants/roles';
import useUser from '../../utility/hooks/useUser';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

function CustomTabPanel({children, value, index, ...other}) {
    return (
        <div
            role="tabpanel"
            style={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}
            hidden={value !== index}
            id={`simple-tabPanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

// eslint-disable-next-line react/prop-types
const IconBadge = ({IconComponent, selected}) => (
    <Box
        sx={{
            width: 36,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            backgroundColor: selected ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            transition: 'background-color 0.2s ease',
            '&:hover': !selected && {
                backgroundColor: 'rgba(255, 255, 255, 0.15)'
            }
        }}>
        <IconComponent sx={{
            fontSize: 22,
            color: '#F0F0F0'
        }}/>
    </Box>
);

const DashboardContainer = styled(Box)(({theme}) => {
    const barHeight = theme.spacing(8);

    return {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: barHeight,
        [theme.breakpoints.up('md')]: {
            paddingBottom: 0,
        },
    };
});

const PageContainer = styled('div')(({theme}) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F0F4F8',
}));

const tabsByRole = {
    [ROLES.SCHOOL_ADMIN]: [
        {
            label: 'Settings',
            route: 'school',
            icon: (selected) => (
                <IconBadge IconComponent={SettingsOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'settings'
        },
        {
            label: 'Profiles',
            route: 'profiles',
            icon: (selected) => (
                <IconBadge IconComponent={GroupsOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'profile'
        },
        {
            label: 'Forms',
            route: 'forms',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        },
        {
            label: 'Reports',
            route: 'reports',
            icon: (selected) => (
                <IconBadge IconComponent={AssessmentOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'reports'
        },
        {
            label: 'Documents',
            route: 'documents',
            icon: (selected) => (
                <IconBadge IconComponent={DescriptionOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'docs'
        }
    ],
    [ROLES.MEMBER_OF_STAFF]: [
        {
            label: 'Profiles',
            route: 'profiles',
            icon: (selected) => (
                <IconBadge IconComponent={GroupsOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'profile'
        },
        {
            label: 'Forms',
            route: 'forms',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        },
        {
            label: 'Reports',
            route: 'reports',
            icon: (selected) => <IconBadge IconComponent={AssessmentOutlinedIcon} selected={selected}/>,
            orderKey: ''
        },
        {
            label: 'Documents',
            route: 'documents',
            icon: (selected) => (
                <IconBadge IconComponent={DescriptionOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'docs'
        }
    ],
    [ROLES.SENIOR_MEMBER_OF_STAFF]: [
        {
            label: 'Profiles',
            route: 'profiles',
            icon: (selected) => (
                <IconBadge IconComponent={GroupsOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'profile'
        },
        {
            label: 'Forms',
            route: 'forms',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        },
        {
            label: 'Reports',
            route: 'reports',
            icon: (selected) => (
                <IconBadge IconComponent={AssessmentOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'reports'
        },
        {
            label: 'Documents',
            route: 'documents',
            icon: (selected) => (
                <IconBadge IconComponent={DescriptionOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'docs'
        }
    ],
    [ROLES.EMPLOYEE]: [
        {
            label: 'My Profile',
            route: 'my-profile',
            icon: (selected) => (
                <IconBadge IconComponent={AccountCircleOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'profile'
        },
        {
            label: 'My Forms',
            route: 'request-and-responses',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentIndOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        }
    ],
    [ROLES.PARENT]: [
        {
            label: 'Profiles',
            route: 'profiles',
            icon: (selected) => (
                <IconBadge IconComponent={GroupsOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'profile'
        },
        {
            label: 'My Forms',
            route: 'request-and-responses',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentIndOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        }
    ],
    [ROLES.STUDENT]: [
        {
            label: 'My Forms',
            route: 'request-and-responses',
            icon: (selected) => (
                <IconBadge IconComponent={AssignmentIndOutlinedIcon} selected={selected}/>
            ),
            orderKey: 'forms'
        }
    ],
    DEFAULT: []
};

const groupTabsByOrderKey = tabs => {
    const groups = new Map();

    for (const tab of tabs) {
        let key = tab.orderKey;

        if (!groups.has(key)) groups.set(key, []);

        groups.get(key).push(tab);
    }

    return Array.from(groups.values()).flat();
};

const getTabsByUserRoles = roles => {
    const tabs = new Map();
    const sortedRoles = [...roles].sort(
        (a, b) => (ROLE_IDS[a] ?? 99) - (ROLE_IDS[b] ?? 99)
    );

    sortedRoles.forEach(role => {
        const roleTabs = tabsByRole[role] || tabsByRole.DEFAULT;

        roleTabs.forEach(tab => {
            if (!tabs.has(tab.route)) {
                tabs.set(tab.route, tab);
            }
        });
    });

    return groupTabsByOrderKey(Array.from(tabs.values()));
};

const SchoolDashboardPage = () => {
    const {user} = useUser();
    const tabs = getTabsByUserRoles(user.roles);
    const locationPath = useLocation().pathname;
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const primaryTabs = tabs.filter((t) => ['Profiles', 'My Profile', 'Forms'].includes(t.label));
    const overflowTabs = tabs.filter((t) => !primaryTabs.includes(t));
    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const isMoreMenuOpen = Boolean(moreAnchorEl);

    const handleMoreClick = (event) => setMoreAnchorEl(event.currentTarget);
    const handleMoreClose = () => setMoreAnchorEl(null);

    const activeRoute =
        tabs.find((tab) => locationPath.includes(tab.route))?.route ??
        (tabs[0]?.route || '');

    const tabsValue =
        isSmallScreen && overflowTabs.some((t) => t.route === activeRoute)
            ? 'more'
            : activeRoute;

    const isSettingsPage = locationPath.includes('/school');
    const isOverflowSelected = tabsValue === 'more';
    const isFormsPage = /^\/(forms|[^/]+\/forms)$/.test(locationPath);

    useEffect(() => {
        if (user.roles?.includes(ROLES.PARENT)) {
            dispatch(retrieveProfilesByParent({pageNumber: 0, pageSize: 20}));
        }
    }, [user.roles, dispatch]);

    return (
        <DashboardContainer>
            <Tabs
                value={tabsValue}
                variant={'fullWidth'}
                textColor="inherit"
                TabIndicatorProps={{style: {display: 'none'}}}
                sx={{
                    width: '100%',
                    minHeight: 0,
                    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.12), #0DAD95)',
                    backgroundColor: '#0DAD95',
                    ...(isSmallScreen && {
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                    }),
                }}
            >
                {(isSmallScreen ? primaryTabs : tabs).map((tab) => {
                    const selected = tabsValue === tab.route;

                    return (
                        <Tab
                            key={tab.route}
                            value={tab.route}
                            data-testid={`nav-${tab.route}`}
                            icon={tab.icon(selected)}
                            label={
                                <Typography
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: selected ? 700 : 500,
                                        fontSize: {xs: '10px', sm: '13.5px'},
                                        letterSpacing: '0.3px',
                                        color: selected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.60)',
                                    }}
                                >
                                    {tab.label}
                                </Typography>
                            }
                            iconPosition="top"
                            component={NavLink}
                            to={tab.route}
                            sx={{
                                flex: 1,
                                opacity: selected ? 1 : 0.95,
                                minWidth: 0,
                                minHeight: 0,
                                // Maria, 19 October 2026: a little more space above and below
                                paddingTop: 1.25,
                                paddingBottom: 1,
                                paddingLeft: 6,
                                paddingRight: 6,
                            }}
                        />
                    );
                })}

                {isSmallScreen && overflowTabs.length > 0 && (
                    <Tab
                        key="more"
                        value="more"
                        icon={<IconBadge IconComponent={MoreHorizIcon} selected={isOverflowSelected}/>}
                        iconPosition="top"
                        label={
                            <Typography
                                sx={{
                                    fontSize: {xs: '10px', sm: '13.5px'},
                                    color: isOverflowSelected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.60)',
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: isOverflowSelected ? 700 : 500,
                                    letterSpacing: '0.3px',
                                }}
                            >
                                More
                            </Typography>
                        }
                        onClick={handleMoreClick}
                        sx={{
                            opacity: isOverflowSelected ? 1 : 0.95,
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            paddingTop: 1.25,
                            paddingBottom: 1,
                            paddingLeft: 6,
                            paddingRight: 6,
                        }}
                    />
                )}
            </Tabs>

            <Menu
                anchorEl={moreAnchorEl}
                open={isMoreMenuOpen}
                onClose={handleMoreClose}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                {overflowTabs.map((tab) => (
                    <MenuItem
                        key={tab.route}
                        data-testid={`nav-${tab.route}`}
                        component={NavLink}
                        to={tab.route}
                        onClick={handleMoreClose}
                        selected={activeRoute === tab.route}
                        sx={{
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(13, 173, 149, 0.12)',
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(13, 173, 149, 0.18)',
                            },
                        }}
                    >
                        {tab.label}
                    </MenuItem>
                ))}
            </Menu>

            <PageContainer
                sx={
                    isSettingsPage || isFormsPage
                        ? {
                            px: 0,
                            '& > :first-child': {mt: 0, pt: 0},
                        }
                        : undefined
                }
            >
                <Outlet/>
            </PageContainer>
        </DashboardContainer>
    );
};

export default SchoolDashboardPage;
